import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Edit, Trash2, Eye, EyeOff, AlertCircle, BookOpen } from 'lucide-react'
import { fetchBlogPosts, updateBlogPost, deleteBlogPost } from '../utils/api'
import type { BlogPost } from '../types'

export function AdminBlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [toggling, setToggling] = useState<string | null>(null)
  const [deleting, setDeleting] = useState<string | null>(null)

  useEffect(() => {
    loadPosts()
  }, [])

  const loadPosts = async () => {
    try {
      const data = await fetchBlogPosts(true)
      setPosts(data)
    } catch {
      setError('Erro ao carregar artigos')
    } finally {
      setLoading(false)
    }
  }

  const handleToggle = async (post: BlogPost) => {
    setToggling(post.id)
    try {
      await updateBlogPost(post.id, { enabled: !post.enabled })
      setPosts((prev) =>
        prev.map((p) => (p.id === post.id ? { ...p, enabled: !p.enabled } : p))
      )
    } catch {
      alert('Erro ao atualizar artigo')
    } finally {
      setToggling(null)
    }
  }

  const handleDelete = async (id: string, title: string) => {
    if (!confirm(`Tem a certeza que deseja eliminar o artigo "${title}"?`)) {
      return
    }

    setDeleting(id)
    try {
      await deleteBlogPost(id)
      setPosts((prev) => prev.filter((p) => p.id !== id))
    } catch {
      alert('Erro ao eliminar artigo')
    } finally {
      setDeleting(null)
    }
  }

  return (
    <div className="flex-1 py-6 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestão do Blog</h1>
            <p className="text-sm text-gray-500">Gerir artigos e conteúdo do blog</p>
          </div>
          <Link to="/admin/blog/new" className="btn-primary gap-2">
            <Plus className="w-5 h-5" />
            Novo Artigo
          </Link>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3 mb-6">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <div className="card">
          <div className="p-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">Artigos do Blog</h2>
            <p className="text-sm text-gray-500">{posts.length} artigo(s)</p>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
            </div>
          ) : posts.length === 0 ? (
            <div className="p-8 text-center">
              <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">Nenhum artigo criado ainda.</p>
              <Link to="/admin/blog/new" className="btn-primary inline-flex gap-2">
                <Plus className="w-5 h-5" />
                Criar Primeiro Artigo
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {posts.map((post) => (
                <div
                  key={post.id}
                  className={`p-4 flex items-center justify-between hover:bg-gray-50 transition-colors ${
                    !post.enabled ? 'opacity-60' : ''
                  }`}
                >
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-5 h-5 text-primary-600" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900 truncate">{post.title}</h3>
                        {!post.enabled && (
                          <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">
                            Desativado
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 truncate">
                        /{post.slug} · {post.category} · {post.read_time}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleToggle(post)}
                      disabled={toggling === post.id}
                      className={`p-2 rounded-lg transition-colors ${
                        post.enabled
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-gray-400 hover:bg-gray-100'
                      }`}
                      title={post.enabled ? 'Desativar' : 'Ativar'}
                    >
                      {toggling === post.id ? (
                        <div className="w-5 h-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      ) : post.enabled ? (
                        <Eye className="w-5 h-5" />
                      ) : (
                        <EyeOff className="w-5 h-5" />
                      )}
                    </button>
                    <Link
                      to={`/admin/blog/${post.id}`}
                      className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      title="Editar"
                    >
                      <Edit className="w-5 h-5" />
                    </Link>
                    <button
                      onClick={() => handleDelete(post.id, post.title)}
                      disabled={deleting === post.id}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      title="Eliminar"
                    >
                      {deleting === post.id ? (
                        <div className="w-5 h-5 animate-spin rounded-full border-2 border-red-600 border-t-transparent" />
                      ) : (
                        <Trash2 className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6">
          <Link to="/admin" className="text-primary-600 hover:text-primary-700 text-sm">
            ← Voltar ao painel
          </Link>
        </div>
      </div>
    </div>
  )
}
